import React, {useState, useEffect} from 'react';
import styled from 'styled-components';

interface FolderInfo {
    name: string;
    title: string;
    imageCount: number;
}

const PortfolioContainer = styled.div`
    padding: 2rem 10rem;
    max-width: 1200px;
    margin: 0 auto;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        flex-direction: column;
        padding: 2rem 0;
    }
`;

const CardGrid = styled.div`
    display: grid;
    margin-top: 10px;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 2rem;
`;

const Card = styled.div`
    cursor: pointer;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;

    &:hover {
        transform: translateY(-5px);
    }
`;

const CardImage = styled.img`
    width: 100%;
    height: 300px;
    object-fit: cover;
`;

const CardTitle = styled.h3`
    padding: 1rem;
    margin: 0;
    text-align: center;
`;

const Modal = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background-color: white;
    padding: 2rem 5rem;
    border-radius: 8px;
    max-width: 60%;
    max-height: 80%;
    overflow-y: auto;
    position: relative;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
        max-width: 80%;
        padding: 2rem;
    }
`;

const CloseButton = styled.button`
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    padding: 0.5rem;
    line-height: 1;
`;

const ImageGallery = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
`;

const GalleryImage = styled.img`
    max-width: 100%;
    max-height: 600px;
    object-fit: cover;
    border-radius: 4px;

    @media (max-width: 768px) {
        width: 100%;
        max-height: none;
    }
`;

const Portfolio: React.FC = () => {
    const [folders, setFolders] = useState<FolderInfo[]>([]);
    const [selectedFolder, setSelectedFolder] = useState<FolderInfo | null>(null);

    useEffect(() => {
        const folderList: FolderInfo[] = [
            {name: '5_monet_garden_space_decoration', title: 'Monet\'s Garden Space Decoration', imageCount: 5},
            {name: '3_wildflowers_decoration', title: 'Wildflower Decoration', imageCount: 3},
            {name: '2_summer_garden_wedding_arch', title: 'Summer Garden Wedding Arch', imageCount: 2},
            {
                name: '6_elegant_mystical_hanging_decoration',
                title: 'Elegant & Mystical Hanging Decoration',
                imageCount: 6
            },
            {name: '4_flower_styling_in_fairy_tales', title: 'Flower Styling in Fairy Tales', imageCount: 4},
            {name: '5_forest_jungle_table_styling', title: 'Forest Jungle Table Styling', imageCount: 5},
            {name: '9_autumn_pumpkin_flower_styling', title: 'Autumn Pumpkin Flower Styling', imageCount: 9},
            {
                name: '4_watercolor_rainbow_hanging_installation',
                title: 'Watercolor Rainbow Hanging Installation',
                imageCount: 4
            },
            {name: '3_dolphin_sea_flower_decoration', title: 'Dolphin & Sea Flower Decoration', imageCount: 3},
            {name: '2_flower_shoes', title: 'Flower Shoes', imageCount: 2},
            {name: '3_chair_decoration', title: 'Chair Decoration', imageCount: 3},
            {name: '5_autumn_wedding_arch', title: 'Autumn Wedding Arch', imageCount: 5},
            {name: '3_color_palette_wedding_arch', title: 'Color Palette Wedding Arch', imageCount: 3},
            {
                name: '6_hydrangea_forest_hanging_installation',
                title: 'Hydrangea Forest Hanging Installation',
                imageCount: 6
            },
            {name: '3_rustic_hanging_decoration', title: 'Rustic Hanging Decoration', imageCount: 3},
            {
                name: '3_grand_budapest_hotel_inspiration_flower_decoration',
                title: 'Grand Budapest Hotel Inspiration Flower Decoration',
                imageCount: 3
            },
            {name: '8_flower_umbrella', title: 'Flower Umbrella', imageCount: 8},
            {name: '2_lilac_flower_cloud', title: 'Lilac Flower Cloud', imageCount: 2},
            {name: '6_rainbow_wall_floor_decoration', title: 'Rainbow Wall Floor Decoration', imageCount: 6},
            {
                name: '7_van_gogh_inspiration_flower_styling',
                title: 'Van Gogh Inspiration Flower Styling',
                imageCount: 7
            },
            {name: '3_wonderland_flower_styling', title: 'Wonderland Flower Styling', imageCount: 3},
            {name: '4_christmas_forest_decoration', title: 'Christmas Forest Decoration', imageCount: 4},
            {name: '3_earth_hanging_installation', title: 'Earth Hanging Installation', imageCount: 3},
            {name: '2_flower_tree', title: 'Flower Tree', imageCount: 2},
            {name: '4_spring_garden_table_styling', title: 'Spring Garden Table Styling', imageCount: 4},
            {name: '5_smoketree_hanging_decoration', title: 'Smoketree Hanging Decoration', imageCount: 5},
            {name: '4_spring_garden_wedding_arches', title: 'Spring Garden Wedding Arches', imageCount: 4},
            {name: '6_blue_hydrangea_wedding_arch', title: 'Blue Hydrangea Wedding Arch', imageCount: 6},
            {name: '6_flower_cloud', title: 'Flower Cloud', imageCount: 6},
            {name: '2_mini_arch', title: 'Mini Arch', imageCount: 2}
        ];
        setFolders(folderList);
    }, []);

    const openModal = (folder: FolderInfo): void => {
        setSelectedFolder(folder);
    };

    const closeModal = (): void => {
        setSelectedFolder(null);
    };

    return (
        <PortfolioContainer>
            <h2>Portfolio</h2>
            <CardGrid>
                {folders.map((folder) => (
                    <Card key={folder.name} onClick={() => openModal(folder)}>
                        <CardImage
                            src={`${process.env.PUBLIC_URL}/portfolio/${folder.name}/1.jpg`}
                            alt={folder.title}
                        />
                        <CardTitle>{folder.title}</CardTitle>
                    </Card>
                ))}
            </CardGrid>
            {selectedFolder && (
                <Modal onClick={closeModal}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                        <CloseButton onClick={closeModal}>&times;</CloseButton>
                        <h2>{selectedFolder.title}</h2>
                        <ImageGallery>
                            {[...Array(selectedFolder.imageCount)].map((_, index) => (
                                <GalleryImage
                                    key={index}
                                    src={`${process.env.PUBLIC_URL}/portfolio/${selectedFolder.name}/${index + 1}.jpg`}
                                    alt={`${selectedFolder.title} - Image ${index + 1}`}
                                />
                            ))}
                        </ImageGallery>
                    </ModalContent>
                </Modal>
            )}
        </PortfolioContainer>
    );
};

export default Portfolio;